/**
 * Project Role Changes Types v1.0.0
 * 
 * Type definitions for the project role mutation governance system including:
 * - Project roles and hierarchy
 * - Risk evaluation for role changes
 * - Audit log entries
 * - Receipt payloads
 * 
 * @module types/projectRoleChanges
 * @see PROJECT_ROLE_CHANGES_SPEC.md
 */

// =============================================================================
// SYSTEM VERSION
// =============================================================================

/**
 * Frozen public API version. Include in receipts and debug logs.
 */
export const PROJECT_ROLE_CHANGES_SYSTEM_VERSION = '1.0.0';

// =============================================================================
// PROJECT ROLE TYPES
// =============================================================================

/**
 * Canonical project-wide participant assignments.
 * Company participation (Investee/Investor/Partner/Vendor) is NOT a person role.
 *
 * Legacy Title-Case values (Investor/Vendor/Company/Partner) remain readable
 * through migrateLegacyMembershipRole() until membership rows are backfilled.
 */
export type ProjectRole =
  | 'Project Manager'
  | 'Contributor'
  | 'Observer'
  /** @deprecated Legacy membership seat — migrate to Contributor/Observer + company participation. */
  | 'Investor'
  /** @deprecated Legacy membership seat — migrate to Contributor + company participation. */
  | 'Vendor'
  /** @deprecated Legacy membership seat — migrate to Observer + company participation. */
  | 'Company'
  /** @deprecated Legacy membership seat — migrate to Contributor/Observer + company participation. */
  | 'Partner';

/** Roles offered in the Project Participants selector. */
export type CanonicalProjectAssignmentRole =
  | 'Project Manager'
  | 'Contributor'
  | 'Observer';

export const CANONICAL_PROJECT_ASSIGNMENT_ROLES: readonly CanonicalProjectAssignmentRole[] = [
  'Project Manager',
  'Contributor',
  'Observer',
] as const;

/**
 * Project role hierarchy from highest power to lowest.
 * Used for risk evaluation.
 */
export const PROJECT_ROLE_HIERARCHY: ProjectRole[] = [
  'Project Manager',
  'Contributor',
  'Observer',
  'Investor',
  'Partner',
  'Vendor',
  'Company',
];

/**
 * Role power levels for risk calculation.
 */
export const PROJECT_ROLE_POWER: Record<ProjectRole, number> = {
  'Project Manager': 100,
  Contributor: 60,
  Observer: 30,
  Investor: 80,
  Partner: 60,
  Vendor: 50,
  Company: 30,
};

/**
 * External (non-internal) roles.
 */
const EXTERNAL_TOKEN_CAPITAL = 'Investor';
const EXTERNAL_TOKEN_ALLY = 'Partner';
const EXTERNAL_TOKEN_SUPPLIER = 'Vendor';
export const EXTERNAL_ROLES: ProjectRole[] = [
  'Contributor',
  'Observer',
  EXTERNAL_TOKEN_CAPITAL,
  EXTERNAL_TOKEN_ALLY,
  EXTERNAL_TOKEN_SUPPLIER,
  'Company',
];

/**
 * Internal roles (full team members).
 */
export const INTERNAL_ROLES: ProjectRole[] = ['Project Manager'];

// =============================================================================
// RISK TYPES
// =============================================================================

/**
 * Risk level for project role changes.
 * - low: Safe changes (e.g., Client ↔ Partner)
 * - medium: Notable changes (e.g., promotion to BDM)
 * - high: Critical changes (e.g., promotion to Project Manager, contractor → internal)
 */
export type ProjectRoleRiskLevel = 'low' | 'medium' | 'high';

/**
 * Risk reasons for project role changes.
 */
export type ProjectRoleRiskReason =
  | 'promotion_to_project_manager'
  | 'external_to_manager'
  | 'external_role_promotion'
  | 'promotion_to_investor'
  | 'demotion_from_privileged'
  | 'removing_privileged_role'
  | 'removing_active_contributor';

/**
 * Result of risk evaluation for role changes.
 */
export interface ProjectRoleRiskEvaluation {
  level: ProjectRoleRiskLevel;
  reasons: ProjectRoleRiskReason[];
  requires_two_step_confirm: boolean;
  suggested_justification_min_length: number;
  current_role: ProjectRole | null;
  new_role?: ProjectRole | null;
  is_promotion?: boolean;
  is_external?: boolean;
  is_privilege_escalation?: boolean;
  action: 'role_change' | 'remove';
}

// =============================================================================
// CHANGE LOG TYPES
// =============================================================================

/**
 * Type of change recorded in the log.
 */
export type ProjectRoleChangeType = 'role_change' | 'member_removed';

/**
 * Audit log entry for project role changes.
 */
export interface ProjectRoleChangeLogEntry {
  id: string;
  workspace_id: string;
  project_id: string;
  actor_user_id: string;
  target_user_id: string;
  change_type: ProjectRoleChangeType;
  old_role: ProjectRole | null;
  new_role: ProjectRole | null;
  risk_level: ProjectRoleRiskLevel;
  risk_reasons: ProjectRoleRiskReason[];
  justification: string | null;
  created_at: string;
  receipt_id: string;
  version: string;
}

/**
 * Log entry with embedded user details for UI display.
 */
export interface ProjectRoleChangeLogEntryWithUsers extends ProjectRoleChangeLogEntry {
  actor?: {
    id: string;
    name: string | null;
    email: string;
  };
  target?: {
    id: string;
    name: string | null;
    email: string;
  };
}

// =============================================================================
// RPC REQUEST/RESPONSE TYPES
// =============================================================================

/**
 * Request payload for changing a project member's role.
 */
export interface ChangeProjectRoleRequest {
  project_id: string;
  target_user_id: string;
  new_role: ProjectRole;
  justification?: string;
}

/**
 * Request payload for removing a project member.
 */
export interface RemoveProjectMemberRequest {
  project_id: string;
  target_user_id: string;
  justification?: string;
}

/**
 * Request payload for risk evaluation preview.
 */
export interface EvaluateRoleChangeRiskRequest {
  project_id: string;
  target_user_id: string;
  new_role: ProjectRole | null; // null for removal
  action: 'role_change' | 'remove';
}

/**
 * Successful role change response.
 */
export interface ChangeProjectRoleSuccess {
  success: true;
  log_id: string;
  action: 'role_changed';
  old_role: ProjectRole | null;
  new_role: ProjectRole;
  risk_level: ProjectRoleRiskLevel;
  risk_reasons: ProjectRoleRiskReason[];
  receipt: ProjectRoleChangeReceipt;
}

/**
 * Idempotent response when role is already set.
 */
export interface ChangeProjectRoleUnchanged {
  success: true;
  action: 'unchanged';
  message: string;
  current_role: ProjectRole;
  new_role: ProjectRole;
}

/**
 * Error response for role change failures.
 */
export interface ChangeProjectRoleError {
  success: false;
  error: {
    code: 'UNAUTHORIZED' | 'INVALID_ROLE' | 'PROJECT_NOT_FOUND' | 'CAPABILITY_DENIED' | 'SELF_ROLE_CHANGE' | 'JUSTIFICATION_REQUIRED';
    message: string;
  };
}

/**
 * Union type for all role change responses.
 */
export type ChangeProjectRoleResponse = 
  | ChangeProjectRoleSuccess 
  | ChangeProjectRoleUnchanged 
  | ChangeProjectRoleError;

/**
 * Successful member removal response.
 */
export interface RemoveMemberSuccess {
  success: true;
  log_id: string;
  action: 'member_removed';
  removed_role: ProjectRole;
  risk_level: ProjectRoleRiskLevel;
  risk_reasons: ProjectRoleRiskReason[];
  receipt: ProjectMemberRemovalReceipt;
}

/**
 * Idempotent response when user is not a member.
 */
export interface RemoveMemberUnchanged {
  success: true;
  action: 'unchanged';
  message: string;
}

/**
 * Error response for member removal failures.
 */
export interface RemoveMemberError {
  success: false;
  error: {
    code: 'UNAUTHORIZED' | 'PROJECT_NOT_FOUND' | 'CAPABILITY_DENIED' | 'SELF_REMOVAL' | 'JUSTIFICATION_REQUIRED';
    message: string;
  };
}

/**
 * Union type for all member removal responses.
 */
export type RemoveMemberResponse = 
  | RemoveMemberSuccess 
  | RemoveMemberUnchanged 
  | RemoveMemberError;

/**
 * Response for role change history query.
 */
export interface RoleChangeHistoryResponse {
  success: true;
  records: ProjectRoleChangeLogEntryWithUsers[];
  total: number;
  limit: number;
  offset: number;
}

// =============================================================================
// RECEIPT TYPES
// =============================================================================

/**
 * Receipt payload for project role changes.
 * Immutable proof of mutation for audit/legal purposes.
 */
export interface ProjectRoleChangeReceipt {
  receipt_id: string;
  receipt_type: 'project_role_change';
  version: string;
  log_id: string;
  workspace_id: string;
  project_id: string;
  target_user_id: string;
  actor_user_id: string;
  old_role: ProjectRole | null;
  new_role: ProjectRole;
  changed_at: string;
  risk_level: ProjectRoleRiskLevel;
}

/**
 * Receipt payload for project member removals.
 */
export interface ProjectMemberRemovalReceipt {
  receipt_id: string;
  receipt_type: 'project_member_removal';
  version: string;
  log_id: string;
  workspace_id: string;
  project_id: string;
  target_user_id: string;
  actor_user_id: string;
  removed_role: ProjectRole;
  removed_at: string;
  risk_level: ProjectRoleRiskLevel;
}

/**
 * Union type for all project role change receipts.
 */
export type ProjectRoleReceipt = ProjectRoleChangeReceipt | ProjectMemberRemovalReceipt;

// =============================================================================
// UI STATE TYPES
// =============================================================================

/**
 * Modal state for two-step confirmation flow.
 */
export interface TwoStepConfirmState {
  step: 1 | 2;
  riskEvaluation: ProjectRoleRiskEvaluation | null;
  justification: string;
  confirmed: boolean;
}

/**
 * State for role change modal.
 */
export interface ChangeRoleModalState {
  isOpen: boolean;
  targetUser: {
    id: string;
    name: string;
    email: string;
    currentRole: ProjectRole;
  } | null;
  selectedRole: ProjectRole | null;
  twoStep: TwoStepConfirmState;
  isLoading: boolean;
  error: string | null;
}

/**
 * State for remove member modal.
 */
export interface RemoveMemberModalState {
  isOpen: boolean;
  targetUser: {
    id: string;
    name: string;
    email: string;
    currentRole: ProjectRole;
  } | null;
  twoStep: TwoStepConfirmState;
  isLoading: boolean;
  error: string | null;
}

// =============================================================================
// HELPER FUNCTIONS (CLIENT-SIDE)
// =============================================================================

/**
 * Get the power level for a project role.
 */
export function getProjectRolePower(role: ProjectRole | null): number {
  if (!role) return 0;
  return PROJECT_ROLE_POWER[role] ?? 0;
}

/**
 * Check if a role is external (non-internal).
 */
export function isExternalRole(role: ProjectRole): boolean {
  return EXTERNAL_ROLES.includes(role);
}

/**
 * Check if a role is internal.
 */
export function isInternalRole(role: ProjectRole): boolean {
  return INTERNAL_ROLES.includes(role);
}

/**
 * Check if a role change is a promotion.
 */
export function isPromotion(oldRole: ProjectRole | null, newRole: ProjectRole): boolean {
  return getProjectRolePower(newRole) > getProjectRolePower(oldRole);
}

/**
 * Check if a role change is a demotion.
 */
export function isDemotion(oldRole: ProjectRole | null, newRole: ProjectRole): boolean {
  return getProjectRolePower(newRole) < getProjectRolePower(oldRole);
}

/**
 * Client-side risk evaluation preview.
 * Mirrors server-side logic for instant UI feedback.
 */
export function evaluateRoleChangeRiskPreview(
  currentRole: ProjectRole | null,
  newRole: ProjectRole | null,
  action: 'role_change' | 'remove'
): ProjectRoleRiskEvaluation {
  const reasons: ProjectRoleRiskReason[] = [];
  let level: ProjectRoleRiskLevel = 'low';

  const currentPower = getProjectRolePower(currentRole);
  const newPower = newRole ? getProjectRolePower(newRole) : 0;
  const isPromotionChange = newPower > currentPower;
  const isExternal = (currentRole && isExternalRole(currentRole)) || (newRole && isExternalRole(newRole));

  // Handle removal action
  if (action === 'remove') {
    if (currentPower >= 80) {
      level = 'high';
      reasons.push('removing_privileged_role');
    } else if (currentPower >= 50) {
      level = 'medium';
      reasons.push('removing_active_contributor');
    }

    return {
      level,
      reasons,
      requires_two_step_confirm: level === 'high',
      suggested_justification_min_length: level === 'high' ? 20 : level === 'medium' ? 10 : 0,
      current_role: currentRole,
      action: 'remove',
    };
  }

  // Handle role change
  if (!newRole) {
    return {
      level: 'low',
      reasons: [],
      requires_two_step_confirm: false,
      suggested_justification_min_length: 0,
      current_role: currentRole,
      new_role: null,
      action: 'role_change',
    };
  }

  // Rule 1: Promotion to Project Manager is always HIGH risk
  if (newRole === 'Project Manager' && isPromotionChange) {
    level = 'high';
    reasons.push('promotion_to_project_manager');
  }
  // Rule 2: Moving an external participant into project management is high risk.
  else if (currentRole && isExternalRole(currentRole) && isInternalRole(newRole)) {
    level = 'high';
    reasons.push('external_to_manager');
  }
  // Rule 3: Any promotion involving external roles = MEDIUM risk
  else if (isExternal && isPromotionChange) {
    if (level === 'low') level = 'medium';
    reasons.push('external_role_promotion');
  }
  // Rule 4: Promotion to Investor eligibility is a notable access change.
  else if (newRole === EXTERNAL_TOKEN_CAPITAL && isPromotionChange) {
    if (level === 'low') level = 'medium';
    reasons.push('promotion_to_investor');
  }

  // Rule 5: Demotions from privileged roles add medium risk
  if (!isPromotionChange && currentPower >= 80) {
    if (level === 'low') level = 'medium';
    reasons.push('demotion_from_privileged');
  }

  return {
    level,
    reasons,
    requires_two_step_confirm: level === 'high',
    suggested_justification_min_length: level === 'high' ? 20 : level === 'medium' ? 10 : 0,
    current_role: currentRole,
    new_role: newRole,
    is_promotion: isPromotionChange,
    is_external: !!isExternal,
    is_privilege_escalation: level === 'high' && isPromotionChange,
    action: 'role_change',
  };
}

/**
 * Human-readable description of a risk reason.
 */
export function getRiskReasonDescription(reason: ProjectRoleRiskReason): string {
  const descriptions: Record<ProjectRoleRiskReason, string> = {
    promotion_to_project_manager: 'Promoting to Project Manager grants full project control',
    external_to_manager: 'Granting an external participant project-management authority',
    external_role_promotion: 'Promoting an external role increases access',
    promotion_to_investor: 'Granting Investor eligibility changes capital-governance access',
    demotion_from_privileged: 'Demoting from a privileged role reduces their access',
    removing_privileged_role: 'Removing a privileged team member from the project',
    removing_active_contributor: 'Removing an active contributor from the project',
  };
  return descriptions[reason] ?? reason;
}

/**
 * Get color class for risk level badge.
 */
export function getRiskLevelColor(level: ProjectRoleRiskLevel): string {
  const colors: Record<ProjectRoleRiskLevel, string> = {
    low: 'bg-status-success-soft text-status-success-foreground',
    medium: 'bg-status-warning-soft text-status-warning-foreground',
    high: 'bg-status-critical-soft text-status-critical-foreground',
  };
  return colors[level];
}
